import {
  StargraveLeader,
  StargravePermanentInjury,
  StargraveAdvancementChoice,
  StargraveExperienceAchievements,
  StargraveImprovementType,
  StargraveStatLine,
  StargraveSurvivalOutcome,
} from '../types';
import { stargravePermanentInjuries } from '../data/stargrave/permanentInjuries';
import { stargraveBackgrounds, stargravePowers } from '../data/stargrave/coreRules';

export interface StargraveSurvivalResolution {
  memberId: string;
  outcome: StargraveSurvivalOutcome;
  injuryId?: string;

}

export function getSoldierSurvivalOutcome(roll: number): StargraveSurvivalOutcome {
  if (roll < 1 || roll > 20) throw new Error('Survival roll must be between 1 and 20.');
  if (roll <= 4) return 'dead';
  if (roll <= 8) return 'badlyWounded';
  return 'fullRecovery';
}

export function getLeaderSurvivalOutcome(roll: number, captainBonus = false): StargraveSurvivalOutcome {
  if (roll < 1 || roll > 20) throw new Error('Survival roll must be between 1 and 20.');
  const adjustedRoll = Math.min(20, roll + (captainBonus ? 1 : 0));
  if (adjustedRoll <= 2) return 'dead';
  if (adjustedRoll <= 4) return 'permanentInjury';
  if (adjustedRoll <= 6) return 'badlyWounded';
  if (adjustedRoll <= 8) return 'closeCall';
  return 'fullRecovery';
}

export function getEffectiveLeaderStats(leader: StargraveLeader): StargraveStatLine {
  const stats = { ...leader.stats };
  for (const injuryId of leader.permanentInjuryIds) {
    const injury = stargravePermanentInjuries.find((candidate) => candidate.id === injuryId);
    if (!injury?.statModifiers) continue;
    for (const [stat, modifier] of Object.entries(injury.statModifiers)) {
      const key = stat as keyof StargraveStatLine;
      stats[key] += modifier ?? 0;
    }
  }
  return stats;
}

export function canAddPermanentInjury(
  existingIds: string[],
  injury: StargravePermanentInjury
): boolean {
  return existingIds.filter((id) => id === injury.id).length < injury.maximum;
}

export function getStargraveImprovementOptions(newLevel: number): StargraveImprovementType[] {
  const digit = ((newLevel % 10) + 10) % 10;
  if ([1, 3, 4, 6, 8, 9].includes(digit)) return ['activation'];
  if ([2, 7].includes(digit)) return ['stat'];
  if (digit === 5) return ['newPower'];
  return ['newPower', 'stat'];
}

export function canAdvanceStargraveLeader(
  role: 'captain' | 'firstMate',
  captainLevel: number,
  firstMateLevel: number
): boolean {
  return role === 'captain'
    ? captainLevel + 1 - firstMateLevel <= 20
    : firstMateLevel + 1 <= captainLevel - 5;
}

export function advanceStargraveLeader(
  leader: StargraveLeader,
  choice: StargraveAdvancementChoice
): StargraveLeader {
  const newLevel = leader.level + 1;
  if (!getStargraveImprovementOptions(newLevel).includes(choice.type)) {
    throw new Error('That improvement is not available at this level.');
  }

  if (choice.type === 'stat') {
    if (!choice.stat) throw new Error('Select a stat to improve.');
    const maxima = { move: 7, fight: 6, shoot: 6, will: 8, health: 25 };
    if (leader.stats[choice.stat] >= maxima[choice.stat]) throw new Error('That stat is already at its maximum.');
    return {
      ...leader,
      level: newLevel,
      stats: { ...leader.stats, [choice.stat]: leader.stats[choice.stat] + 1 },
      gameState: choice.stat === 'health'
        ? { ...leader.gameState, currentHealth: leader.gameState.currentHealth + 1 }
        : leader.gameState,
    };
  }

  if (!choice.powerId) throw new Error('Select a power.');
  if (choice.type === 'activation') {
    const selectedPower = leader.powers.find((power) => power.powerId === choice.powerId);
    if (!selectedPower) throw new Error('The leader does not know that power.');
    if (selectedPower.activation <= 4) throw new Error('Activation cannot be lowered below 4.');
    return {
      ...leader,
      level: newLevel,
      powers: leader.powers.map((power) => power.powerId === choice.powerId
        ? { ...power, activation: power.activation - 1 }
        : power),
    };
  }

  if (leader.powers.some((power) => power.powerId === choice.powerId)) throw new Error('The leader already knows that power.');
  const power = stargravePowers.find((candidate) => candidate.id === choice.powerId);
  const background = stargraveBackgrounds.find((candidate) => candidate.id === leader.backgroundId);
  if (!power || !background) throw new Error('Select a valid power.');
  return {
    ...leader,
    level: newLevel,
    powers: [...leader.powers, {
      powerId: power.id,
      activation: power.activation + (background.corePowerIds.includes(power.id) ? 0 : 2),
      strain: power.strain,
    }],
  };
}

export function calculateStargraveExperience(
  achievements: StargraveExperienceAchievements,
  enforceLimits = false
): number {
  const nonNegative = (value: number) => Math.max(0, Math.floor(value));
  const powerExperience = nonNegative(achievements.powersActivated) * 10;
  const rivalExperience = nonNegative(achievements.rivalCrewKnockouts) * 10;
  const creatureExperience = nonNegative(achievements.creatureKnockouts) * 5;
  const total =
    nonNegative(achievements.gamesPlayed) * 30
    + (achievements.hadFriendlyKnockout ? 30 : 0)
    + nonNegative(achievements.lootUnlocked) * 20
    + (enforceLimits ? Math.min(powerExperience, 100) : powerExperience)
    + (enforceLimits ? Math.min(rivalExperience, 40) : rivalExperience)
    + (enforceLimits ? Math.min(creatureExperience, 20) : creatureExperience)
    + nonNegative(achievements.scenarioBonus);
  return enforceLimits ? Math.min(total, 300) : total;
}
